import { useTranslation } from 'react-i18next';
import {
  formatWeight,
  nextAutoInstanceValue,
  type KnownInstance,
} from '@jackedlogs/shared';
import { ChoiceTray } from '../index';

import { machineLabel } from './machineLabel';
import type { WeightUnit } from './state/loggerDraft';
import { useTheme } from './theme';

// EQ-1 — the machine chooser, in-place, never a modal over a modal.
//
// ⭐ Why the options carry a LOAD and not just a letter (Antenore, 2026-08-13):
// with four lat pulldowns in one gym, "Machine A / B / C / D" is unusable —
// next session you cannot recall which was which, and an invented name like
// "dual-cable" only helps while you still remember inventing it. The load you
// put on the machine is the one description you do not have to remember: you
// are standing in front of the stack, you read it off, you pick the matching
// row.
//
// It is also the content of the split rather than a label for it. Equipment
// identity exists so two machines with different resistance stop polluting one
// RPE/e1RM series, so the load IS the difference being recorded.
//
// The value stored stays anonymous and automatic (`machine-a`): a stored
// identity must not depend on the language or the spelling of whoever created
// it, and nothing has to be anonymised before a coach sees it.
//
// ⛔ The tray CHROME is no longer here (2026-08-13). It moved to
// <ChoiceTray> in @jackedlogs/ui when the gym chip became its second caller —
// this file kept only what is actually about machines: the load signature and
// the automatic naming. It also stops taking the host screen's StyleSheet as a
// prop, which is what pinned it to one screen.
type Props = {
  known: readonly KnownInstance[];
  /**
   * The default machine's history, when it has one. Only its load signature
   * is read: the default is offered whether or not it has one.
   */
  fallback?: { topSet?: KnownInstance['topSet'] };
  /**
   * Fires with the chosen value.
   *
   * ⛔ Still no "no machine" option, and this is NOT its return. That one meant
   * *decline to say* — it was removed on 2026-08-13 because Cancel already
   * leaves the picker unchanged, and folding the default into it would put two
   * meanings on one tap again.
   */
  onChoose: (value: string) => void;
  /**
   * Fires when the DEFAULT is chosen — a positive answer, not a refusal: the
   * machine the athlete was on before any of them had a name.
   *
   * ⭐ The P0 of 2026-08-14: naming a machine used to be a one-way door.
   * `knownEquipmentInstances` skipped untagged entries, so the default was not
   * in the list to go back to. It is a separate callback rather than a `null`
   * through `onChoose` for the same reason <GymChip> made that choice on the
   * places one surface over: the two concepts stay two, and no caller can pass
   * a value where an absence was meant.
   */
  onClear: () => void;
  /**
   * The machine this entry is on right now, when the host knows it (the
   * current-exercise line does; a list row is about a different exercise and
   * passes nothing).
   *
   * Marked as selected so reopening the tray answers "wait, was it A or B?"
   * without making the athlete remember — the same question the load
   * signature answers for the machine in front of them.
   */
  activeInstance?: string | null;
  /**
   * Every value already spoken for, for the automatic naming of a NEW machine.
   *
   * ⚠️ Defaults to `known`, which is what it always implicitly was — it became
   * a separate prop with EQ-1-GYM-COLDSTART R6, when `known` stopped being the
   * whole set. A machine declared absent here is not OFFERED but is still
   * NAMED, so minting off the offered list alone would hand its letter to a
   * second, different machine and merge two histories under one identity.
   */
  takenValues?: readonly string[];
  unit: WeightUnit;
  theme: ReturnType<typeof useTheme>;
  /**
   * EQ-1-PIN-DEFAULT — the ★ on each row: which machine wins here from now on,
   * so the tray stops asking (Antenore, 2026-08-20).
   *
   * ⛔ Offered on the DEFAULT row too, and that row is the one that matters
   * most: with two machines known the resolver interrupts every single time,
   * and electing the untagged one is how the athlete says "my usual one, stop
   * asking". A ★ only on named machines would leave that case with no answer.
   *
   * ⛔ Omitted entirely when the place is unknown, exactly as the ✕ is: an
   * election with no room to hold it has nowhere to be stored. Absent prop =
   * no ★ anywhere, and the tray renders as it did before.
   *
   * `pinned` is three-valued like its source: a slug, `null` for the elected
   * Default, `undefined` for nothing elected.
   */
  pin?: {
    pinned: string | null | undefined;
    /** `null` elects the Default; a slug elects that machine; re-tapping the
     *  one already in force un-elects and restores the question. */
    onToggle: (value: string | null) => void;
  };
};

export function MachineTray({
  known,
  fallback,
  onChoose,
  onClear,
  activeInstance,
  takenValues,
  unit,
  theme,
  pin,
}: Props) {
  const { t, i18n } = useTranslation();

  // "Machine A · 80 kg × 8". One line, so it still fits the house <Chip> and
  // needs no bespoke two-line option row. A brand-new instance has no
  // signature until its first set is logged, and then reads as its name alone.
  const withLoad = (name: string, topSet: KnownInstance['topSet']): string => {
    if (!topSet) return name;
    const load = formatWeight(topSet.weightKg, unit, i18n.language);
    return `${name} · ${load} × ${topSet.reps}`;
  };

  return (
    <ChoiceTray
      theme={theme}
      label={t('swap.whichMachine', { defaultValue: 'Which machine?' })}
      // It carries its load signature like the rest — the default is the one
      // the athlete uses most, so it is the one a bare label would fail hardest
      // to describe. Not through machineLabel(): that turns a `machine-b` slug
      // into its letter, and the default has no slug to turn.
      //
      // ⛔ `activeInstance === null`, not `!activeInstance`: a list row is about
      // a DIFFERENT exercise and passes nothing, and `undefined` there means
      // "the host does not know", which must not read as "on the default".
      defaultOption={{
        label: withLoad(
          t('swap.defaultMachine', { defaultValue: 'Default machine' }),
          fallback?.topSet,
        ),
        selected: activeInstance === null,
        onChoose: onClear,
        ...(pin
          ? {
              action: {
                active: pin.pinned === null,
                // ⛔ The label follows the STATE, not the control: a toggle
                // that announces the same thing pressed and unpressed leaves a
                // screen-reader user with no way to know which way it is.
                accessibilityLabel:
                  pin.pinned === null
                    ? t('swap.unpinDefaultMachine', {
                        defaultValue:
                          'Stop always using the default machine here',
                      })
                    : t('swap.pinDefaultMachine', {
                        defaultValue: 'Always use the default machine here',
                      }),
                // ⛔ The hint follows the state too, not just the label. With
                // the ★ lit, "stops asking" describes what is ALREADY true,
                // while the tap would do the opposite — a label and a hint
                // pointing opposite ways is worse than no hint.
                accessibilityHint:
                  pin.pinned === null
                    ? t('swap.unpinHint', {
                        defaultValue:
                          'Goes back to asking which machine, at this gym, for this exercise.',
                      })
                    : t('swap.pinHint', {
                        defaultValue:
                          'Stops asking which machine, at this gym, for this exercise.',
                      }),
                onPress: () => pin.onToggle(null),
              },
            }
          : {}),
      }}
      options={known.map((k) => ({
        key: k.value,
        label: withLoad(machineLabel(k.value, t), k.topSet),
        selected: k.value === activeInstance,
        ...(pin
          ? {
              action: {
                active: pin.pinned === k.value,
                accessibilityLabel:
                  pin.pinned === k.value
                    ? t('swap.unpinMachine', {
                        machine: machineLabel(k.value, t),
                        defaultValue: 'Stop always using {{machine}} here',
                      })
                    : t('swap.pinMachine', {
                        machine: machineLabel(k.value, t),
                        defaultValue: 'Always use {{machine}} here',
                      }),
                accessibilityHint:
                  pin.pinned === k.value
                    ? t('swap.unpinHint', {
                        defaultValue:
                          'Goes back to asking which machine, at this gym, for this exercise.',
                      })
                    : t('swap.pinHint', {
                        defaultValue:
                          'Stops asking which machine, at this gym, for this exercise.',
                      }),
                onPress: () => pin.onToggle(k.value),
              },
            }
          : {}),
      }))}
      // ⛔ Said in WORDS, not left to a small filled star. The election is the
      // whole state of this tray and it changes which machine a set is
      // attributed to; a reader with reduced acuity should not have to spot a
      // glyph to know it is in force.
      {...(pin && pin.pinned !== undefined
        ? {
            footnote:
              pin.pinned === null
                ? t('swap.pinnedFootnoteDefault', {
                    defaultValue: 'Always uses the default machine here.',
                  })
                : t('swap.pinnedFootnoteMachine', {
                    machine: machineLabel(pin.pinned, t),
                    defaultValue: 'Always uses {{machine}} here.',
                  }),
          }
        : {})}
      onChoose={onChoose}
      // One tap, no keyboard: the next automatic name for this exercise.
      // Names are per exercise, so the leg press's A never collides with the
      // pec deck's. ⚠️ Minted off `takenValues`, not off what is on screen:
      // see the prop's doc — a hidden machine still owns its letter.
      create={{
        label: t('swap.newMachine', { defaultValue: 'New machine' }),
        onPress: () =>
          onChoose(
            nextAutoInstanceValue(takenValues ?? known.map((k) => k.value)),
          ),
      }}
    />
  );
}
